import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, ArrowRight, Check, Crown, Search, FileText, Reply,
  ShieldCheck, LayoutDashboard, TrendingUp, Star, Quote,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ScheduleNudge from "../components/ScheduleNudge";
import { CONCIERGE_SERVICES, CONCIERGE_TIERS, HERO_IMAGES, FAQS, TESTIMONIALS } from "../lib/mockData";

const ICONS = { Search, FileText, Reply, ShieldCheck, LayoutDashboard, TrendingUp };

const TIMELINE = [
  { w: "Week 1", title: "Discovery", desc: "Eligibility + prior-art search, claim strategy session with senior counsel." },
  { w: "Week 2", title: "Drafting", desc: "Complete specification with provisional or full filing — your call." },
  { w: "Week 3", title: "Filing", desc: "Submission at IPO. Optional simultaneous PCT for global protection." },
  { w: "Months 6-18", title: "Prosecution", desc: "Every examiner objection answered. You stay informed, we handle everything." },
  { w: "Year 1-20", title: "Grant & Renewal", desc: "Renewal automation + annual review with your IP manager." },
];

export default function Concierge() {
  return (
    <div className="surface-cream min-h-screen" data-testid="concierge-page">
      <Header/>

      {/* HERO */}
      <section className="relative overflow-hidden surface-deep text-white pt-16 pb-20">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] blob bg-[#0E6E5E]"/>
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] blob bg-[#F5D442]"/>
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div>
            <div className="chip chip-gold inline-flex mb-6"><Crown size={12}/> Premium Concierge Service</div>
            <h1 className="font-display-tight text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] mb-6">
              From sketch <br/>
              to grant. <br/>
              <span className="text-[#7CC242]">We handle everything.</span>
            </h1>
            <p className="text-lg text-white/70 max-w-xl leading-relaxed mb-8">
              For founders, scientists, and corporates who'd rather invent than juggle attorneys.
              Our concierge team owns the entire patent journey — drafting, filing, prosecution, grant.
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              <button className="btn-accent">Schedule a Discovery Call <ArrowRight size={14}/></button>
              <Link to="/submit" className="btn-outline bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0A2520]">Or submit a brief</Link>
            </div>
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/10 max-w-md">
              <S label="Filings managed" value="3,200+"/>
              <S label="Grant rate" value="97%"/>
              <S label="Avg. weeks saved" value="11"/>
            </div>
          </div>
          <div className="relative">
            <img src={HERO_IMAGES.meeting} alt="" className="rounded-3xl aspect-[4/5] object-cover w-full"/>
            <div className="absolute -bottom-6 -right-6 bg-[#7CC242] text-[#0A2520] p-6 rounded-2xl max-w-[240px]">
              <Sparkles size={18} className="mb-3"/>
              <div className="font-display font-bold text-lg leading-tight mb-1">"They filed 14 patents for us in 6 months."</div>
              <div className="text-xs">— Kavya Iyer, NovaCircuit</div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="chip inline-flex mb-3">What's included</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2520] mb-3">Everything. Truly everything.</h2>
          <p className="text-[#5A6B66]">A single Udhyak engagement covers every milestone — from idea evaluation to renewal, across geographies.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CONCIERGE_SERVICES.map((s) => {
            const Icon = ICONS[s.icon] || Sparkles;
            return (
              <div key={s.title} className="card p-7">
                <div className="w-12 h-12 rounded-xl surface-sage flex items-center justify-center text-[#0E6E5E] mb-4">
                  <Icon size={20}/>
                </div>
                <div className="font-display font-bold text-lg text-[#0A2520] mb-2">{s.title}</div>
                <p className="text-sm text-[#5A6B66] leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-white py-20 border-y border-[#E1E8E2] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30"/>
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="chip mb-3">The Concierge timeline</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2520] mb-3">A predictable, transparent timeline.</h2>
            <p className="text-[#5A6B66]">No mystery, no scope creep, no surprise invoices. Everything mapped from week one.</p>
          </div>
          <div className="space-y-4">
            {TIMELINE.map((t, i) => (
              <div key={i} className="card p-6 grid md:grid-cols-[120px_1fr_auto] gap-5 items-center">
                <div className="font-mono text-sm text-[#0E6E5E] font-bold">{t.w}</div>
                <div>
                  <div className="font-display font-bold text-lg text-[#0A2520] mb-1">{t.title}</div>
                  <p className="text-sm text-[#5A6B66] leading-relaxed">{t.desc}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#0E6E5E] text-white flex items-center justify-center font-display font-bold text-lg flex-shrink-0">{i + 1}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="chip inline-flex mb-3">Concierge pricing</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2520] mb-3">One fixed price. Zero surprises.</h2>
          <p className="text-[#5A6B66]">Pay for the outcome, not the hours. All taxes and government filing fees included.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {CONCIERGE_TIERS.map((t) => (
            <div key={t.name} className={`card p-8 relative ${t.best ? "ring-2 ring-[#0E6E5E] shadow-xl" : ""}`} data-testid={`concierge-tier-${t.name}`}>
              {t.best && <div className="absolute -top-3 left-1/2 -translate-x-1/2 chip chip-dark">Most chosen</div>}
              <div className="font-display font-bold text-xl text-[#0A2520] mb-1">{t.name}</div>
              <div className="mb-6 pt-3">
                <span className="font-display font-extrabold text-5xl text-[#0A2520]">{t.price}</span>
                <span className="text-sm text-[#5A6B66] ml-2">{t.per}</span>
              </div>
              <ul className="space-y-3 mb-7">
                {t.feat.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-[#3D4F4A]">
                    <Check size={16} className="text-[#0E6E5E] flex-shrink-0 mt-0.5"/> {f}
                  </li>
                ))}
              </ul>
              <button className={t.best ? "btn-primary w-full justify-center" : "btn-outline w-full justify-center"}>Get started</button>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIAL CALLOUT */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 pb-12">
        <div className="card-flat surface-deep text-white p-10 lg:p-14 rounded-3xl border-[#0A2520]">
          <Quote size={36} className="text-[#7CC242] mb-6"/>
          <p className="font-display text-2xl sm:text-3xl leading-relaxed mb-8 max-w-3xl">
            "{TESTIMONIALS[2].quote}"
          </p>
          <div className="flex items-center gap-4">
            <img src={TESTIMONIALS[2].avatar} alt="" className="w-12 h-12 rounded-full object-cover"/>
            <div>
              <div className="font-display font-bold">{TESTIMONIALS[2].name}</div>
              <div className="text-sm text-white/60">{TESTIMONIALS[2].role} · {TESTIMONIALS[2].city}</div>
            </div>
            <div className="ml-auto flex gap-1">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} className="fill-[#7CC242] text-[#7CC242]"/>)}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-12">
          <div>
            <div className="chip mb-3">Common questions</div>
            <h2 className="font-display font-bold text-3xl text-[#0A2520] mb-5 leading-tight">Questions you might have.</h2>
            <p className="text-[#5A6B66] mb-6">Can't find the answer? Our concierge team is one call away.</p>
            <button className="btn-primary text-sm">Talk to an expert</button>
          </div>
          <div className="space-y-3">
            {FAQS.map((f) => (
              <details key={f.q} className="card p-5 group">
                <summary className="font-display font-semibold text-[#0A2520] cursor-pointer flex items-center justify-between gap-3">
                  <span>{f.q}</span>
                  <ChevronToggle/>
                </summary>
                <p className="text-sm text-[#5A6B66] mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16">
        <ScheduleNudge variant="dark" message="Let our concierge team handle the rest."/>
      </section>

      <Footer/>
    </div>
  );
}

function S({ label, value }) {
  return (
    <div>
      <div className="font-display font-extrabold text-3xl text-[#7CC242] mb-1">{value}</div>
      <div className="text-xs text-white/60 uppercase tracking-widest">{label}</div>
    </div>
  );
}

function ChevronToggle() {
  return (
    <svg className="transition-transform group-open:rotate-180 flex-shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
  );
}
